import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { Button } from "@mui/material";
import GenericFormPopup from "../GenericFormPopup/GenericFormPopup";
import {
  makeFieldConfigDynamic,
  updateIncidentFieldConfiguration,
} from "../../../formConfigs/Fleet/incidentFieldConfig";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import StatusSnackbar from "../../General/StatusSnackbar/StatusSnackbar";
import { capitalizeFirstChar } from "../../../helpers/formatingFunctions";
import { normalizeIncidentTypeValue } from "../../../data/Fleet/IncidentType";
import { getId } from "../../HR/UpdateEmployeePopup/UpdateEmployeePopup";
import { fetchAllDrivers, fetchAssets } from "../IncidentPopup/IncidentPopup";

const normalizeLocationValue = (location: string) =>
  location.toLowerCase() === "unknown" ? "Unknown" : location.toUpperCase();

const UpdateIncidentPopup = ({ incident }: { incident: any }) => {
  const [open, setOpen] = useState(false);
  const [selectedAssetType, setSelectedAssetType] = useState<string>(
    incident.truckId ? "Truck" : "Trailer",
  );
  const [initialValues, setInitialValues] = useState<any>({
    assetAffected: incident.truckId ? "Truck" : "Trailer",
    openDate: incident.openDate.split("T")[0],
    complaint: incident.complaint,
    receivedBy: incident.receivedBy,
    location: normalizeLocationValue(incident.location),
    incidentType: normalizeIncidentTypeValue(
      capitalizeFirstChar(incident.incidentType),
    ),
  });
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const handleOpen = (event: { stopPropagation: () => void }) => {
    event.stopPropagation();
    setOpen(true);
  };
  const handleClose = () => setOpen(false);
  const handleSnackbarClose = () => setStatus("idle");
  const queryClient = useQueryClient();
  const { token } = useAuth();

  const { data: assetData } = useQuery({
    queryKey: ["assets", selectedAssetType],
    queryFn: () => fetchAssets(selectedAssetType, token),
    enabled: open,
  });

  const { data: allDrivers } = useQuery({
    queryKey: ["drivers"],
    queryFn: () => fetchAllDrivers(token),
    enabled: open,
  });

  const incidentMutation = useMutation({
    mutationFn: (data: any) =>
      axios.put(`${apiVariable}/updateIncident`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
    onSuccess: () => {
      setStatus("success");
      handleClose();
      queryClient.invalidateQueries({ queryKey: ["incidents"] });
    },
    onError: (error: any) => {
      setStatus("error");
      setErrorMessage(error.response.data || "Failed to submit incident");
    },
  });

  const submitHandler = (data: any) => {
    const requiredFields = ["complaint", "location", "incidentType"];

    const missingFields = requiredFields.filter((field) => !data[field]);

    if (missingFields.length > 0) {
      setStatus("error");
      setErrorMessage("Please fill in all fields. Coordinates is optional.");
      return;
    }

    const coordinates = data.coordinates
      ? data.coordinates.split(",").map((coord: string) => coord.trim())
      : [null, null];
    const [latitude, longitude] = coordinates;

    const submitData = {
      ...data,
      id: incident.id,
      assetNumber: getId(data.assetNumber),
      driverId: getId(data.driverId),
      latitude,
      longitude,
    };

    delete submitData.coordinates;

    incidentMutation.mutate(submitData);
  };

  useEffect(() => {
    if (!open || !incident) return;

    const formattedCoordinates = incident.coordinates
      .replace(/(\d+),(\d+)/g, "$1.$2")
      .replace(/[()]/g, "");

    const assetId = incident.truckId || incident.trailerId;

    if (!assetData) return;

    const matchingAsset = assetData.find(
      (asset: any) => asset.id === assetId?.toString(),
    );

    if (!allDrivers) return;
    const matchingDriver = allDrivers.find(
      (driver: any) => driver.id === incident.driverId,
    );

    setInitialValues({
      assetAffected: selectedAssetType,
      assetNumber: matchingAsset || null,
      driverId: matchingDriver || null,
      openDate: incident.openDate.split("T")[0],
      complaint: incident.complaint,
      receivedBy: incident.receivedBy,
      location: normalizeLocationValue(incident.location),
      incidentType: normalizeIncidentTypeValue(
        capitalizeFirstChar(incident.incidentType),
      ),
      coordinates: incident.coordinates === "(, )" ? "" : formattedCoordinates,
    });
  }, [open, incident, assetData, allDrivers]);

  const dynamicFields = makeFieldConfigDynamic(
    updateIncidentFieldConfiguration,
    setSelectedAssetType,
    selectedAssetType,
  );

  return (
    <>
      <Button variant="contained" color="primary" onClick={handleOpen}>
        Update
      </Button>
      <GenericFormPopup
        formFields={dynamicFields}
        initialValues={initialValues}
        autocompleteData={{ assets: assetData, drivers: allDrivers }}
        title="Check or update incident's details"
        onSubmit={submitHandler}
        open={open}
        onClose={handleClose}
        loading={incidentMutation.isPending}
      />

      <StatusSnackbar
        status={status}
        successMessage="Incident updated successfully"
        errorMessage={errorMessage}
        handleClose={handleSnackbarClose}
      />
    </>
  );
};

export default UpdateIncidentPopup;
